import { Box, Chip, FormControlLabel, Switch, Tooltip, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useTranslations } from '@/i18n/use-translations.tsx';

type MediaAssetApprovalControlProps = {
  isApproved: boolean;
  canApprove: boolean;
  /** When false, approval stays disabled until the asset is playout-ready. */
  isApprovable?: boolean;
  isPending?: boolean;
  onToggle?: () => void;
};

export const MediaAssetApprovalControl = ({
  isApproved,
  canApprove,
  isApprovable = true,
  isPending = false,
  onToggle,
}: MediaAssetApprovalControlProps) => {
  const t = useTranslations();
  const mayToggleApproval = canApprove && isApprovable;

  if (!isApproved && !canApprove) {
    return null;
  }

  if (isApproved && !canApprove) {
    const readyLabel = t('media.approvalLabelReady');
    return (
      <Tooltip title={t('media.approvalTooltipApproved')} placement="left" disableInteractive>
        <Box
          component="span"
          aria-label={readyLabel}
          sx={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
        >
          <Box
            component="span"
            sx={{
              display: { xs: 'inline-flex', sm: 'none' },
              alignItems: 'center',
              color: 'success.main',
            }}
          >
            <CheckCircleOutlineIcon fontSize="small" />
          </Box>
          <Chip
            size="small"
            color="success"
            variant="outlined"
            icon={<CheckCircleOutlineIcon />}
            label={readyLabel}
            sx={{ display: { xs: 'none', sm: 'inline-flex' }, maxWidth: '100%' }}
          />
        </Box>
      </Tooltip>
    );
  }

  const tooltip = isApproved
    ? t('media.approvalTooltipApproved')
    : !isApprovable
      ? t('media.approvalTooltipAwaitingProcessing')
      : t('media.approvalTooltipPending');
  const label = isApproved ? t('media.approvalLabelReady') : t('media.approvalLabelPending');
  const isToggleDisabled = isPending || !mayToggleApproval;
  const showTaskPrimary = !isApproved && mayToggleApproval && !isPending;

  return (
    <Tooltip title={tooltip} placement="left">
      <FormControlLabel
        sx={{
          'mr': 0,
          'ml': 0,
          'gap': 0.5,
          'alignItems': 'center',
          '& .MuiFormControlLabel-label': {
            lineHeight: 1.2,
          },
        }}
        label={
          <Typography
            variant="caption"
            component="span"
            color={isApproved ? 'success.main' : showTaskPrimary ? 'primary.main' : 'text.disabled'}
            sx={{ whiteSpace: 'nowrap', display: { xs: 'none', sm: 'inline' } }}
          >
            {label}
          </Typography>
        }
        labelPlacement="start"
        control={
          <Switch
            size="small"
            color={isApproved ? 'success' : 'primary'}
            checked={isApproved}
            disabled={isToggleDisabled}
            onChange={(e) => {
              e.stopPropagation();
              onToggle?.();
            }}
            onClick={(e) => e.stopPropagation()}
            inputProps={{
              'aria-label': label,
            }}
            sx={
              showTaskPrimary
                ? {
                    '& .MuiSwitch-switchBase': {
                      color: 'primary.main',
                    },
                    '& .MuiSwitch-switchBase + .MuiSwitch-track': {
                      backgroundColor: 'primary.main',
                    },
                  }
                : undefined
            }
          />
        }
      />
    </Tooltip>
  );
};
